import React from 'react'

import { Field, Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Form, Col, Row, Divider, Button } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import CondicionesDePagoConstant from 'constants/condicionesDePago'
import TiposDeProductoConstant from 'constants/tiposDeProducto'
import TipoOrdenCompraConstant from 'constants/tipoOrdenCompra'
import { AfectacionesCompraConstant } from 'constants/index'

import { IdGen, RounderUtil } from 'utils'

import * as OrdenCompraService from 'services/ordenCompra.service'

import * as FormGroups from './formGroups'
import * as Modals from './modals'
import * as Sections from './sections'
import PrevisualizarPdfModal from '../../lista/modals/PrevisualizarPdfModal'

import { ValidationSchema } from './validation'

const actualizarFila = (current, { cantidad, precio, __updatedField, incluyeIGV, ...restPayload }) => {
  current = {
    ...current,
    ...restPayload,
    cantidad,
    precio
  }

  if (__updatedField === 'cantidad' || __updatedField === 'precio') {
    let numCantidad = parseFloat(cantidad ?? '0') || 0
    let numPrecio = parseFloat(precio ?? '0') || 0

    // Bloqueo de negativos a nivel del reducer (defensa adicional al input)
    if (numCantidad < 0) {
      numCantidad = 0
      current.cantidad = '0'
    }
    if (numPrecio < 0) {
      numPrecio = 0
      current.precio = '0'
    }

    if (current._fromRequerimiento && current.cantidadOriginalRequerimiento != null) {
      if (numCantidad > current.cantidadOriginalRequerimiento) {
        numCantidad = current.cantidadOriginalRequerimiento
        current.cantidad = String(numCantidad)
        current.__cantidadClampada = true
      } else {
        current.__cantidadClampada = false
      }
      current.cantidadPendiente = Math.max(0, current.cantidadOriginalRequerimiento - numCantidad)
    }

    const numSubTotal = RounderUtil.round(numCantidad * numPrecio)

    let numIgv = 0

    if (
      current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
      current.afectacion !== AfectacionesCompraConstant.OTROS &&
      incluyeIGV
    ) {
      numIgv = RounderUtil.round(numSubTotal * 0.18)
    }

    current.igv = numIgv
    current.subTotal = numSubTotal.toFixed(2)
  } else if (__updatedField === 'menos-igv') {
    const numPrecio = parseFloat(precio || '0')
    const numNuevoPrecio = RounderUtil.round(numPrecio / 1.18, 4)
    const numPrevSubTotal = parseFloat(current.subTotal || '0')
    const numSubTotal = RounderUtil.round(numPrevSubTotal / 1.18)

    let numIgv = 0

    if (
      current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
      current.afectacion !== AfectacionesCompraConstant.OTROS
    ) {
      numIgv = RounderUtil.round(numPrevSubTotal - numSubTotal)
    }

    current.subTotal = numSubTotal
    current.igv = numIgv
    current.precio = numNuevoPrecio.toFixed(4)
  } else {
    current.precio = ''
    current.subTotal = ''
  }

  return current
}

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          __focusDescuento: false,
          __focusPrecio: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion)

      let { _id, nuevaFila, viaCodBar, ...payload } = action.payload

      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idFila: IdGen.newId(),
          cantidad: '1',
          precio: '0',
          subTotal: '0'
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: `${IdGen.newId()}-G`
        }
      ]
    }
    case 'actualizar': {
      const { saltarFila, ...nuevoPayload } = action.payload
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload)
          }
          val.__filaActiva = !saltarFila
        }
        return val
      })
    }
    case 'actualizarComentario':
      return state.map((val, i) => {
        if (val._idFila === action.payload._idFila) {
          val.comentario = action.payload.comentario
        }
        return val
      })
    case 'menos-igv': {
      return state.map(val => {
        if (!val.esFilaBusqueda) {
          val = actualizarFila(val, {
            ...val,
            __updatedField: 'menos-igv'
          })
        }
        return val
      })
    }
    case 'fill-requerimientos': {
      return action.payload.map(val => {
        const cantidadParaOC = val.cantidadPendiente != null && val.cantidadPendiente > 0
          ? val.cantidadPendiente
          : val.cantidad
        val.cantidadOriginalRequerimiento = cantidadParaOC
        val = actualizarFila(val, {
          ...val,
          cantidad: cantidadParaOC,
          __updatedField: 'precio',
          _idFila: IdGen.newId()
        })

        return val
      })
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    case 'actualizarDetalle': {
      const { _idFila, ...nuevoPayload } = action.payload

      return state.map(val => {
        if (val._idFila === _idFila) {
          return actualizarFila(val, nuevoPayload)
        }
        return val
      })
    }
    default:
      return state
  }
}

const MainForm = ({ history, reiniciarFormulario, editarData }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, innerArr => {
    let output = []

    if (innerArr) {
      output = innerArr.map((x, k) => ({
        ...x,
        _idFila: k
      }))
    }

    output.push({
      esFilaBusqueda: true,
      _idFila: IdGen.newId()
    })

    return output
  })

  const [mostrarRequerimientosxSocioModal, setMostrarRequerimientosxSocioModal] = React.useState(false)
  const [mostrarRefrescar, setMostrarRefrescar] = React.useState(false)
  const [tieneRequerimientos, setTieneRequerimientos] = React.useState(false)
  const formikRef = React.useRef(null)
  const [detallesSectionKey, setDetallesSectionKey] = React.useState(IdGen.newId())

  const [mostrarPdfModal, setMostrarPdfModal] = React.useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState('')
  const [mostrarCuotasModal, setMostrarCuotasModal] = React.useState(false)
  const [cuotasSubmitData, setCuotasSubmitData] = React.useState(null)

  const montosFormRef = React.useRef(null)

  const handleClosePdfModal = React.useCallback(() => {
    setMostrarPdfModal(false)
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl('')
    }
    history.push(`/${CodigosTransaccionesConstant.ORDENESCOMPRAS_LISTA}`)
  }, [pdfBlobUrl, history])

  const formInitialValues = React.useMemo(() => {
    return {
      _idOficinaSolicitante: editarData._idOficinaSolicitante
        ? (Array.isArray(editarData._idOficinaSolicitante) ? editarData._idOficinaSolicitante : [editarData._idOficinaSolicitante])
        : [],
      _idContactoSocio: editarData._idContactoSocio ?? '',
      _idDireccionSocio: editarData._idDireccionSocio ?? '',
      _idSocio: editarData._idSocio ?? '',
      _idUsuarioSolicitante: editarData._idUsuarioSolicitante ?? '',
      comentario: editarData.comentario ?? '',
      condicionPago: editarData.condicionPago ?? '',
      contactoObra: editarData.contactoObra ?? '',
      documentoSocio: editarData.documentoSocio ?? '',
      fechaEmision: editarData.fechaEmision ?? __adminContext.fechaTrabajo,
      fechaEntrega: editarData.fechaEntrega ?? __adminContext.fechaTrabajo,
      flagReciboHonorario: editarData.flagReciboHonorario ?? false,
      glosa: editarData.glosa ?? '',
      incluyeIGV: editarData.incluyeIGV ?? false,
      medioPago: editarData.medioPago ?? '',
      moneda: editarData.moneda ?? '',
      nombreSocio: editarData.nombreSocio ?? '',
      numeroCotizacion: editarData.numeroCotizacion ?? '',
      tipo: editarData.tipo ?? TipoOrdenCompraConstant.ORDENCOMPRA,
      banco: editarData.banco ?? '',
      tipoCuenta: editarData.tipoCuenta ?? '',
      numeroCuenta: editarData.numeroCuenta ?? '',
      cci: editarData.cci ?? '',
      aNombreDe: editarData.aNombreDe ?? '',
      numeroContrato: editarData.numeroContrato ?? ''
    }
  }, [__adminContext, editarData])

  const clickMenosIgv = () => {
    detallesDispatcher({ type: 'menos-igv' })
    setDetallesSectionKey(IdGen.newId())
    setMostrarRefrescar(false)
  }

  const clickVerRequerimientos = React.useCallback(() => {
    setMostrarRequerimientosxSocioModal(true)
  }, [])

  const handleRequerimientosFilled = React.useCallback((data) => {
    // Extraer oficinas únicas de los requerimientos
    const oficinas = [...new Set(data.filter(x => x._idOficinaSolicitante).map(x => x._idOficinaSolicitante))]
    if (formikRef.current && oficinas.length > 0) {
      formikRef.current.setFieldValue('_idOficinaSolicitante', oficinas)
    }
    // Extraer usuario solicitante (tomar el primero si hay varios)
    const usuarios = [...new Set(data.filter(x => x._idUsuarioSolicitante).map(x => x._idUsuarioSolicitante))]
    if (formikRef.current && usuarios.length > 0) {
      formikRef.current.setFieldValue('_idUsuarioSolicitante', usuarios[0])
    }
    setTieneRequerimientos(true)
  }, [])

  const ejecutarGuardado = React.useCallback((dataBody, { setSubmitting }) => {
    const operacion = editarData._id
      ? () => OrdenCompraService.actualizarxId(editarData._id, dataBody)
      : () => OrdenCompraService.nueva(dataBody)

    operacion()
      .then(({ data: data2 }) => {
        if (editarData._id) {
          __zariContext.snack('success', 'La orden se ha registrado.')
          history.push(`/${CodigosTransaccionesConstant.ORDENESCOMPRAS_LISTA}`)
        } else {
          OrdenCompraService.descargarxId(data2.hash)
            .then(({ data }) => {
              __zariContext.snack('success', 'La orden se ha registrado.')
              const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
              setPdfBlobUrl(url)
              setMostrarPdfModal(true)
            })
            .catch(() => {
              __zariContext.snack('warning', 'La orden se completó pero no se pudo generar la previsualización.')
              history.push(`/${CodigosTransaccionesConstant.ORDENESCOMPRAS_LISTA}`)
            })
        }
      })
      .catch(({ data: { error } }) => {
        __zariContext.snack('error', error)
        setSubmitting(false)
      })
  }, [editarData, __zariContext, history])

  const submitHandler = (data, { setSubmitting }) => {
    const detallesNormalizados = detalles
      .filter(x => !x.esFilaBusqueda)
      .map(x => ({
        _idRequerimientoDetalle: x._idRequerimientoDetalle,
        _idProductoPresentacion: x._idProductoPresentacion,
        _idProyecto: x._idProyecto,
        codigoPresupuesto: x.codigoPresupuesto,
        _idCentroCostoNivel1: x._idCentroCostoSugerido || x._idCentroCostoNivel1 || null,
        cantidad: parseFloat(x.cantidad || 0),
        detalle: x.detalle,
        precio: parseFloat(x.precio || 0),
        subTotal: parseFloat(x.subTotal || 0),
        igv: data.flagReciboHonorario ? 0 : parseFloat(x.igv || 0),
        comentario: x.comentario,
        afectacion: x.afectacion
      }))

    if (!detallesNormalizados || detallesNormalizados.length === 0) {
      __zariContext.snack('warning', 'No se han asignado detalles a la orden.')
      setSubmitting(false)
      return
    }

    // Validar negativos y exceso sobre lo pendiente del requerimiento
    const filasOriginales = detalles.filter(x => !x.esFilaBusqueda)
    for (let i = 0; i < detallesNormalizados.length; i++) {
      const det = detallesNormalizados[i]
      const orig = filasOriginales[i] || {}
      const idx = i + 1

      if (det.cantidad <= 0) {
        __zariContext.snack('error', `Fila ${idx}: la cantidad debe ser mayor a cero.`)
        setSubmitting(false)
        return
      }
      if (det.precio < 0) {
        __zariContext.snack('error', `Fila ${idx}: el precio no puede ser negativo.`)
        setSubmitting(false)
        return
      }
      if (det.subTotal < 0) {
        __zariContext.snack('error', `Fila ${idx}: el subtotal no puede ser negativo.`)
        setSubmitting(false)
        return
      }
      if (orig._fromRequerimiento && orig.cantidadOriginalRequerimiento != null) {
        const max = Number(orig.cantidadOriginalRequerimiento)
        if (det.cantidad > max + 0.0001) {
          __zariContext.snack(
            'error',
            `Fila ${idx}: la cantidad ${det.cantidad} excede el saldo pendiente del requerimiento (${max}).`
          )
          setSubmitting(false)
          return
        }
      }
    }

    let importeTotal = parseFloat(montosFormRef.current.values.importeTotal)

    const dataBody = {
      ...data,
      detalles: detallesNormalizados,
      importeTotal: importeTotal
    }

    // Si es crédito, mostrar modal de cuotas antes de guardar
    if (data.condicionPago === CondicionesDePagoConstant.CREDITO) {
      setSubmitting(false)
      setCuotasSubmitData(dataBody)
      setMostrarCuotasModal(true)
      return
    }

    ejecutarGuardado(dataBody, { setSubmitting })
  }

  return (
    <>
      <Formik innerRef={formikRef} initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={ValidationSchema}>
        {({ errors, isSubmitting, setValues, touched, values, handleChange, handleSubmit, setFieldValue }) => (
          <Form>
            <Row>
              <Col md={5}>
                <FormGroups.TipoFormGroup fIsSubmitting={isSubmitting} values={values} />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <FormGroups.GlosaFormGroup
                  iError={errors.glosa}
                  iTouched={touched.glosa}
                  fIsSubmitting={isSubmitting}
                />
              </Col>
              <Col md={7}>
                <Row>
                  <Col md={6}>
                    <FormGroups.FechaEmisionFormGroup
                      handleChange={handleChange}
                      values={values}
                      setValues={setValues}
                      fIsSubmitting={isSubmitting}
                      iError={errors.fechaEmision}
                      iTouched={touched.fechaEmision}
                    />
                  </Col>
                  <Col md={6}>
                    <FormGroups.FechaEntregaFormGroup
                      fIsSubmitting={isSubmitting}
                      iError={errors.fechaEntrega}
                      iTouched={touched.fechaEntrega}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <FormGroups.ProveedorFormGroup
                  fIsSubmitting={isSubmitting}
                  iValue={values._idSocio}
                  values={values}
                  setValues={setValues}
                />
              </Col>
              <Col md={2}>
                {values._idSocio && (
                  <Button tiny outline color='warning' onClick={clickVerRequerimientos}>
                    Ver Requerimientos
                  </Button>
                )}
              </Col>
              <Col md={2}>
                <FormGroups.FlagReciboHonorarioFormGroup
                  setFieldValue={setFieldValue}
                  setMostrarRefrescar={setMostrarRefrescar}
                  fIsSubmitting={isSubmitting}
                  iValue={values.flagReciboHonorario}
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <FormGroups.DireccionSocioFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors._idDireccionSocio}
                  iTouched={touched._idDireccionSocio}
                  setFieldValue={setFieldValue}
                  vIdSocio={values._idSocio}
                />
                <FormGroups.ContactoSocioFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors._idContactoSocio}
                  iTouched={touched._idContactoSocio}
                  setFieldValue={setFieldValue}
                  vIdSocio={values._idSocio}
                />
                <FormGroups.NumeroCotizacionFormGroup
                  iError={errors.numeroCotizacion}
                  iTouched={touched.numeroCotizacion}
                  fIsSubmitting={isSubmitting}
                />
              </Col>
              <Col md={7}>
                <Row>
                  <Col md={6}>
                    <FormGroups.CondicionDePagoFormGroup
                      iError={errors.condicionPago}
                      iTouched={touched.condicionPago}
                      fIsSubmitting={isSubmitting}
                    />
                    <FormGroups.MedioPagoFormGroup
                      iError={errors.medioPago}
                      iTouched={touched.medioPago}
                      fIsSubmitting={isSubmitting}
                    />
                    <FormGroups.MonedaFormGroup
                      iError={errors.moneda}
                      iTouched={touched.moneda}
                      fIsSubmitting={isSubmitting}
                    />
                  </Col>
                  <Col md={6}>
                    {values.condicionPago === CondicionesDePagoConstant.CREDITO && (
                      <Form.Group>
                        <Form.Label text='N° CONTRATO' htmlFor='input-ncontrato' md={4} alignRightAt='md' />
                        <Col md={8}>
                          <Field name='numeroContrato'>
                            {({ field }) => (
                              <Form.InputText {...field} id='input-ncontrato' disabled={isSubmitting} placeholder='N° Contrato' />
                            )}
                          </Field>
                        </Col>
                      </Form.Group>
                    )}
                    <FormGroups.OficinaFormGroup
                      iError={errors._idOficinaSolicitante}
                      iTouched={touched._idOficinaSolicitante}
                      fIsSubmitting={isSubmitting}
                      disabled={tieneRequerimientos}
                    />
                    <FormGroups.UsuarioSolicitanteFormGroup
                      iError={errors._idUsuarioSolicitante}
                      iTouched={touched._idUsuarioSolicitante}
                      fIsSubmitting={isSubmitting}
                      vIdOficinaSolicitante={values._idOficinaSolicitante}
                      vIdUsuarioSolicitante={values._idUsuarioSolicitante}
                      disabled={tieneRequerimientos}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            {values.condicionPago === CondicionesDePagoConstant.CREDITO && (
              <>
                <Divider title='Datos de Crédito' />
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label text='BANCO' htmlFor='input-banco' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='banco'>
                          {({ field }) => (
                            <Form.InputText {...field} id='input-banco' disabled={isSubmitting} placeholder='Nombre del banco' />
                          )}
                        </Field>
                      </Col>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label text='TIPO CTA.' htmlFor='input-tipocuenta' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='tipoCuenta'>
                          {({ field }) => (
                            <Form.Select {...field} id='input-tipocuenta' disabled={isSubmitting}>
                              <option value=''>Seleccionar</option>
                              <option value='AHORRO'>AHORRO</option>
                              <option value='CORRIENTE'>CORRIENTE</option>
                              <option value='CTA SUELDO'>CTA SUELDO</option>
                              <option value='CTA FREE'>CTA FREE</option>
                            </Form.Select>
                          )}
                        </Field>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label text='N° CUENTA' htmlFor='input-numcuenta' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='numeroCuenta'>
                          {({ field }) => (
                            <Form.InputText {...field} id='input-numcuenta' disabled={isSubmitting} placeholder='N° de cuenta' />
                          )}
                        </Field>
                      </Col>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label text='CCI' htmlFor='input-cci' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='cci'>
                          {({ field }) => (
                            <Form.InputText {...field} id='input-cci' disabled={isSubmitting} placeholder='Código interbancario' />
                          )}
                        </Field>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label text='A NOMBRE DE' htmlFor='input-anombredde' md={4} alignRightAt='md' />
                      <Col md={8}>
                        <Field name='aNombreDe'>
                          {({ field }) => (
                            <Form.InputText {...field} id='input-anombredde' disabled={isSubmitting} placeholder='Titular de la cuenta' />
                          )}
                        </Field>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            <Sections.DetallesSection
              key={detallesSectionKey}
              detalles={detalles}
              dispatch={detallesDispatcher}
              tipoDeProductos={
                values.tipo === TipoOrdenCompraConstant.ORDENCOMPRA
                  ? TiposDeProductoConstant.BIEN
                  : TiposDeProductoConstant.SERVICIO
              }
              incluyeIGV={!values.flagReciboHonorario}
              setMostrarRefrescar={setMostrarRefrescar}
            />
            <Divider />
            {mostrarRefrescar && (
              <Row className='mb-2'>
                <Col md='2' className='offset-md-10'>
                  <Button block onClick={e => setMostrarRefrescar(false)}>
                    Recalcular
                  </Button>
                </Col>
              </Row>
            )}
            <Row className='mt-2'>
              <Col md={6}>
                <FormGroups.ComentarioFormGroup
                  iError={errors.comentario}
                  iTouched={touched.comentario}
                  fIsSubmitting={isSubmitting}
                />
                <Divider />
                <Row>
                  <Col md={6}>
                    <Button className='ml-2' onClick={clickMenosIgv} block>
                      - IGV
                    </Button>
                  </Col>
                </Row>
              </Col>
              <Col md={6}>
                <Sections.ResumenMontosSection
                  innerRef={montosFormRef}
                  detalles={mostrarRefrescar ? [] : detalles.filter(x => !x.esFilaBusqueda)}
                  incluyeIGV={!values.flagReciboHonorario}
                />
              </Col>
            </Row>
            <Divider />
            <Row>
              <Col md={12} className='text-right'>
                <Button color='secondary' disabled={isSubmitting} onClick={reiniciarFormulario}>
                  Cancelar
                </Button>
                <Button disabled={isSubmitting || mostrarRefrescar} onClick={handleSubmit}>
                  Guardar
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {mostrarRequerimientosxSocioModal && (
        <Modals.RequerimientosxSocioModal
          show={mostrarRequerimientosxSocioModal}
          handleClose={() => setMostrarRequerimientosxSocioModal(false)}
          dispatch={detallesDispatcher}
          onFilled={handleRequerimientosFilled}
        />
      )}
      {mostrarPdfModal && (
        <PrevisualizarPdfModal show={mostrarPdfModal} handleClose={handleClosePdfModal} pdfBlobUrl={pdfBlobUrl} />
      )}
      {mostrarCuotasModal && cuotasSubmitData && (
        <Modals.AsignacionCuotasModal
          show={mostrarCuotasModal}
          handleClose={() => setMostrarCuotasModal(false)}
          submitData={cuotasSubmitData}
          submitFunction={ejecutarGuardado}
        />
      )}
    </>
  )
}

export default MainForm
